import React, { useLayoutEffect, useRef, useState } from 'react';

// First-visit tour of the main tiles. Positions come from the tiles' real boxes (not fixed percentages),
// so the spotlight and bubble line up on any phone height and re-measure on resize/scroll.
const STEPS = [
  { target: '.box.calendar', title: '두벅이와 함께', text: '며칠째 공부하는지, 연속 학습 기록과 통계를 여기서 볼 수 있어요.' },
  { target: '.box.study', title: '매일 한 걸음', text: '오늘의 레슨만 누르면 돼요. 새 단어 10개와 오늘의 회화 표현을 6분 만에 끝내요.' },
  { target: '.box.test', title: '잊기 전에 복습', text: <>두벅이가 잊어버리기 직전인 단어만 골라 줘요.<br />틀린 단어와 챌린지 시험도 여기 있어요.</> },
  { target: '.box.result', title: '말해 보자', text: '뉴욕 여행부터 일상 대화, 문법까지 상황별 회화를 연습할 수 있어요.' },
];
const GAP = 14;    // spotlight ↔ bubble
const MARGIN = 12; // bubble ↔ screen edge

const seen = () => {
  try { return !!localStorage.getItem('tutorial'); } catch (e) { return true; }
};

const Tutorial = () => {
  const [step, setStep] = useState(() => (seen() ? null : 0)); // 0..3 | 'start' | 'stop' | null
  const [back, setBack] = useState(0); // step to return to from 'stop'
  const [box, setBox] = useState(null);
  const bubble = useRef(null);
  const [bubbleH, setBubbleH] = useState(0);
  const cur = typeof step === 'number' ? STEPS[step] : null;

  useLayoutEffect(() => {
    if (!cur) return undefined;
    const el = document.querySelector(cur.target);
    const frame = document.getElementById('container');
    if (!el || !frame) return undefined;
    el.scrollIntoView({ block: 'center' });
    const measure = () => setBox({ el: el.getBoundingClientRect(), frame: frame.getBoundingClientRect() });
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [cur]);

  useLayoutEffect(() => {
    if (bubble.current) setBubbleH(bubble.current.offsetHeight);
  }, [step, box]);

  if (step === null) return null;

  const finish = () => {
    try { localStorage.setItem('tutorial', 'Y'); } catch (e) { /* private mode: it just shows again */ }
    setStep(null);
  };
  const skip = () => { setBack(step); setStep('stop'); };

  if (step === 'start' || step === 'stop') {
    return (
      <div id="popup" className="tutorial-dialog" role="dialog" aria-modal="true">
        <div className="popup-wrap">
          <div className="popup-body">
            {step === 'start' ? <>두벅이와 함께<br />공부를 시작해 보세요.</> : <>튜토리얼은 그만 보고<br />시작할까요?</>}
          </div>
          <div className="popup-btn-group">
            <button type="button" className="btn btn-confirm" onClick={finish}>확인</button>
            {step === 'stop' && <button type="button" className="btn btn-cancel" onClick={() => setStep(back)}>취소</button>}
          </div>
        </div>
        <div className="popup-dim" />
      </div>
    );
  }

  if (!box) return <div className="tutorial-wrap" />;
  const { el, frame } = box;
  // Below the tile when it fits, otherwise above; never off-screen
  const below = el.bottom + GAP + bubbleH <= window.innerHeight - MARGIN;
  const top = below ? el.bottom + GAP : Math.max(MARGIN, el.top - GAP - bubbleH);
  const left = frame.left + 20;
  const width = frame.width - 40;
  const arrowX = Math.min(Math.max(el.left + el.width / 2 - left, 28), width - 28);

  return (
    <div className="tutorial-wrap" role="dialog" aria-modal="true" aria-label={cur.title}>
      <div className={`area${step === 0 ? ' is-calendar' : ''}`} style={{ top: el.top, left: el.left, width: el.width, height: el.height }} />
      <div id="popup" className="style03">
        <div ref={bubble} className={`popup-wrap ${below ? 'is-below' : 'is-above'}`} style={{ top, left, width, '--arrow-x': `${arrowX}px` }}>
          <div className="popup-header">
            <strong className="popup-title">{cur.title}</strong>
            <button type="button" className="popup-close" onClick={skip}><span className="visuallyhidden">닫기</span></button>
          </div>
          <div className="popup-body">{cur.text}</div>
          <div className="popup-btn-group">
            <span className="tutorial-count">{step + 1} / {STEPS.length}</span>
            <button type="button" className="btn btn-next" onClick={() => setStep(step + 1 < STEPS.length ? step + 1 : 'start')}>다음</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Tutorial;
